import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { InlineInputCardWrapper } from '@/features/editor/widgets/ai-component/inputs/shared/shared';
import { toCssDeclarationName } from '@/features/editor/widgets/ai-component/inputs/shared/css-declarations';
import { NumberUnitInput } from '@/features/editor/widgets/ai-component/inputs/shared/number-unit-input';
import { parseSpacing } from '@/features/editor/widgets/ai-component/inputs/parsers';
import { useOnChange } from '@/components/hooks/use-on-change';

export interface CssSpacingInputProps {
  name: string;
  initialValue?: string;
  onChange?: (name: string, value: string) => void;
}

export function CssSpacingInput({
  name,
  initialValue,
  onChange,
}: CssSpacingInputProps) {
  const displayName = useMemo(() => {
    if (name === `margin`) {
      return `Spacing`;
    }
    if (name === `padding`) {
      return `Inner Spacing`;
    }
    return toCssDeclarationName(name);
  }, [name]);

  const {
    top: topDef,
    left: leftDef,
    bottom: bottomDef,
    right: rightDef,
  } = parseSpacing(initialValue || `1px`);

  const [top, setTop] = useState(topDef);
  const [left, setLeft] = useState(leftDef);
  const [bottom, setBottom] = useState(bottomDef);
  const [right, setRight] = useState(rightDef);

  const spacing = useMemo(
    () => `${top} ${left} ${bottom} ${right}`,
    [top, right, bottom, left],
  );

  useOnChange(() => onChange?.(name, spacing), [spacing]);

  return (
    <InlineInputCardWrapper>
      <span>{displayName}</span>
      <RectangleLayout>
        <div />
        <NumberUnitInput initialValue={topDef} onChange={setTop} />
        <div />
        <NumberUnitInput initialValue={leftDef} onChange={setLeft} />
        <Rectangle />
        <NumberUnitInput initialValue={rightDef} onChange={setRight} />
        <div />
        <NumberUnitInput initialValue={bottomDef} onChange={setBottom} />
        <div />
      </RectangleLayout>
    </InlineInputCardWrapper>
  );
}

const Rectangle = styled.div`
  display: flex;
  width: 100%;
  height: 1.5rem;
  border: 1px solid rgb(228, 234, 237);
  box-shadow: rgba(0, 0, 0, 0.11) 0px 2px 4px 0px,
    rgba(97, 121, 129, 0.19) 0px 4px 13px 0px;
  border-radius: 1rem;
`;

const RectangleLayout = styled.div`
  width: 65%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-content: center;
  justify-items: center;
  align-items: center;
  justify-content: center;
  row-gap: 1rem;
  column-gap: 1.5rem;
`;
